export type TemplateCreationStep = "DEFINITION" | "EDIT" | "REVIEW";

export type TemplateCreationCategory =
  | "MARKETING"
  | "UTILITY"
  | "AUTHENTICATION";

export type TemplateCreationUseCase =
  | "MARKETING_DEFAULT"
  | "MARKETING_CATALOG"
  | "MARKETING_FLOW"
  | "MARKETING_CALL_PERMISSION"
  | "UTILITY_DEFAULT"
  | "UTILITY_FLOW"
  | "UTILITY_CALL_PERMISSION"
  | "AUTHENTICATION_OTP";

export interface TemplateCreationWizardState {
  step: TemplateCreationStep;
  category: TemplateCreationCategory | null;
  useCase: TemplateCreationUseCase | null;
}

/** Product-enabled create paths (all other use cases stay visible as Coming soon). */
export const PRODUCT_ENABLED_USE_CASES = [
  "MARKETING_DEFAULT",
  "UTILITY_DEFAULT",
] as const satisfies readonly TemplateCreationUseCase[];

export function isProductEnabledUseCase(
  useCase: TemplateCreationUseCase,
): boolean {
  return (PRODUCT_ENABLED_USE_CASES as readonly string[]).includes(useCase);
}

export function defaultUseCaseForCategory(
  category: TemplateCreationCategory,
): TemplateCreationUseCase | null {
  switch (category) {
    case "MARKETING":
      return "MARKETING_DEFAULT";
    case "UTILITY":
      return "UTILITY_DEFAULT";
    case "AUTHENTICATION":
      return null;
    default: {
      const _exhaustive: never = category;
      return _exhaustive;
    }
  }
}

export function canProceedFromDefinition(
  category: TemplateCreationCategory | null,
  useCase: TemplateCreationUseCase | null,
): boolean {
  if (category === null || useCase === null) return false;
  if (!isUseCaseValidForCategory(useCase, category)) return false;
  return isProductEnabledUseCase(useCase);
}

export function createInitialWizardState(): TemplateCreationWizardState {
  return {
    step: "DEFINITION",
    category: "MARKETING",
    useCase: "MARKETING_DEFAULT",
  };
}

export const USE_CASES_BY_CATEGORY: Record<
  TemplateCreationCategory,
  readonly TemplateCreationUseCase[]
> = {
  MARKETING: [
    "MARKETING_DEFAULT",
    "MARKETING_CATALOG",
    "MARKETING_FLOW",
    "MARKETING_CALL_PERMISSION",
  ],
  UTILITY: ["UTILITY_DEFAULT", "UTILITY_FLOW", "UTILITY_CALL_PERMISSION"],
  AUTHENTICATION: ["AUTHENTICATION_OTP"],
} as const;

export function isUseCaseValidForCategory(
  useCase: TemplateCreationUseCase,
  category: TemplateCreationCategory,
): boolean {
  return (USE_CASES_BY_CATEGORY[category] as readonly string[]).includes(
    useCase,
  );
}

export function categoryForUseCase(
  useCase: TemplateCreationUseCase,
): TemplateCreationCategory {
  if (useCase.startsWith("MARKETING_")) return "MARKETING";
  if (useCase.startsWith("UTILITY_")) return "UTILITY";
  return "AUTHENTICATION";
}